import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, Search, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrintAdvertiserLabel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({
    magazine: '',
    status: '',
    advertiser_id: '',
    name: '',
    company_name: '',
    email: '',
    state: '',
    district: '',
    end_date: ''
  });
  const [labelsData, setLabelsData] = useState([]);
  const [multipleIds, setMultipleIds] = useState(['']);
  const [searchMode, setSearchMode] = useState('filters'); // 'filters' or 'multiple-ids'
  const [dispatchInfo, setDispatchInfo] = useState({}); // Store previous shipment info
  const [showLastShipment, setShowLastShipment] = useState(true); // Page-level toggle
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${API}/all-unique-states`);
        setStates(response.data.states);
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };
    fetchStates();
  }, []);

  const fetchDistricts = async (state) => {
    if (!state) { setDistricts([]); return; }
    try {
      const response = await axios.get(`${API}/all-unique-districts/${encodeURIComponent(state)}`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  // Fetch previous shipment info for all advertisers
  const fetchDispatchInfo = async (advertiserIds) => {
    try {
      const response = await axios.post(`${API}/dispatches/batch-latest`, {
        ids: advertiserIds,
        type: 'advertiser'
      });
      setDispatchInfo(response.data);
    } catch (error) {
      console.error('Error fetching dispatch info:', error);
    }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();

    setLoading(true);
    try {
      const response = await axios.post(`${API}/advertisers/search`, searchCriteria);
      
      if (response.data.length === 0) {
        toast.info('No advertisers found');
        setLabelsData([]);
        return;
      }

      // Sort advertisers by numeric part of ID
      const sortedAdvertisers = response.data.sort((a, b) => {
        const extractNumber = (id) => {
          const match = id.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        return extractNumber(a.advertiser_id) - extractNumber(b.advertiser_id);
      });

      // Generate labels with QR codes
      const labels = [];
      for (const advertiser of sortedAdvertisers) {
        try {
          const qrResponse = await axios.get(`${API}/advertisers/qrcode/${advertiser.advertiser_id}`);
          labels.push({
            ...advertiser,
            qr_code: qrResponse.data.qr_code
          });
        } catch (error) {
          console.error(`Error generating QR for ${advertiser.advertiser_id}:`, error);
        }
      }
      setLabelsData(labels);
      
      // Fetch previous shipment info for all advertisers
      const advertiserIds = labels.map(l => l.advertiser_id);
      await fetchDispatchInfo(advertiserIds);
      
      toast.success(`${labels.length} labels generated`);
    } catch (error) {
      console.error('Error generating labels:', error);
      toast.error('Failed to generate labels');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchCriteria({
      magazine: '',
      status: '',
      advertiser_id: '',
      name: '',
      company_name: '',
      email: '',
      state: '',
      district: '',
      end_date: ''
    });
    setLabelsData([]);
    setMultipleIds(['']);
    setDistricts([]);
  };

  const addIdField = () => {
    if (multipleIds.length < 25) {
      setMultipleIds([...multipleIds, '']);
    } else {
      toast.info('Maximum 25 Advertiser IDs allowed');
    }
  };

  const removeIdField = (index) => {
    const newIds = multipleIds.filter((_, i) => i !== index);
    setMultipleIds(newIds.length > 0 ? newIds : ['']);
  };

  const updateIdField = (index, value) => {
    const newIds = [...multipleIds];
    newIds[index] = value;
    setMultipleIds(newIds);
  };

  const handleMultipleIdsSearch = async (e) => {
    if (e) e.preventDefault();
    
    // Filter out empty IDs
    const validIds = multipleIds.filter(id => id.trim() !== '');
    
    if (validIds.length === 0) {
      toast.error('Please enter at least one Advertiser ID');
      return;
    }

    setLoading(true);
    try {
      toast.info(`Fetching labels for ${validIds.length} advertiser ID(s)...`);
      
      // Fetch advertisers by IDs (exact match, case-insensitive)
      const advertiserPromises = validIds.map(async (id) => {
        const response = await axios.post(`${API}/advertisers/search`, { advertiser_id: id.trim() });
        // Filter for exact match only (case-insensitive)
        const trimmedId = id.trim().toUpperCase();
        return response.data.filter(advertiser => advertiser.advertiser_id.toUpperCase() === trimmedId);
      });
      
      const responses = await Promise.all(advertiserPromises);
      const allAdvertisers = responses.flat();
      
      // Check which IDs were not found
      const foundIds = allAdvertisers.map(a => a.advertiser_id.toUpperCase());
      const notFoundIds = validIds.filter(id => !foundIds.includes(id.trim().toUpperCase()));
      
      if (notFoundIds.length > 0) {
        toast.warning(`Advertiser IDs not found: ${notFoundIds.join(', ')}`);
      }
      
      if (allAdvertisers.length === 0) {
        toast.error('No advertisers found for the entered IDs');
        setLabelsData([]);
        setLoading(false);
        return;
      }

      // Sort advertisers by numeric part of ID
      const sortedAdvertisers = allAdvertisers.sort((a, b) => {
        const extractNumber = (id) => {
          const match = id.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        return extractNumber(a.advertiser_id) - extractNumber(b.advertiser_id);
      });

      // Generate labels with QR codes
      const labels = [];
      for (const advertiser of sortedAdvertisers) {
        try {
          const qrResponse = await axios.get(`${API}/advertisers/qrcode/${advertiser.advertiser_id}`);
          labels.push({
            ...advertiser,
            qr_code: qrResponse.data.qr_code
          });
        } catch (error) {
          console.error(`Error generating QR for ${advertiser.advertiser_id}:`, error);
        }
      }
      
      setLabelsData(labels);
      
      // Fetch previous shipment info for all advertisers
      const advertiserIds = labels.map(l => l.advertiser_id);
      await fetchDispatchInfo(advertiserIds);
      
      toast.success(`${labels.length} labels generated successfully!`);
    } catch (error) {
      console.error('Error generating labels:', error);
      toast.error('Failed to generate labels: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // Set document title for PDF filename
    document.title = 'Advertiser Labels';
    window.print();
    // Reset title after print
    setTimeout(() => { document.title = 'Magazine CRM'; }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8 no-print">
          <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-4xl font-bold" style={{ color: '#059669' }} data-testid="print-label-title">
            Print Advertiser Labels
          </h1>
        </div>

        {labelsData.length === 0 && (
          <div className="form-section mb-8">
            {/* Mode Selector Tabs */}
            <div className="flex space-x-2 mb-6 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setSearchMode('filters')}
                className={`px-6 py-3 font-semibold transition-all ${
                  searchMode === 'filters'
                    ? 'border-b-2 border-green-600 text-green-600'
                    : 'text-gray-500 hover:text-green-600'
                }`}
              >
                Search by Filters
              </button>
              <button
                type="button"
                onClick={() => setSearchMode('multiple-ids')}
                className={`px-6 py-3 font-semibold transition-all ${
                  searchMode === 'multiple-ids'
                    ? 'border-b-2 border-green-600 text-green-600'
                    : 'text-gray-500 hover:text-green-600'
                }`}
              >
                Multiple Advertiser IDs
              </button>
            </div>

            {searchMode === 'filters' ? (
              <form onSubmit={handleSearch}>
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#059669' }}>Search Filters</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <Select value={searchCriteria.magazine} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, magazine: value })}>
                    <SelectTrigger data-testid="magazine-filter">
                      <SelectValue placeholder="Select magazine" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                      <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={searchCriteria.status} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, status: value })}>
                    <SelectTrigger data-testid="status-filter">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Advertiser ID</Label>
                  <Input data-testid="advertiser-id-input" value={searchCriteria.advertiser_id} onChange={(e) => setSearchCriteria({ ...searchCriteria, advertiser_id: e.target.value })} placeholder="Enter advertiser ID" />
                </div>

                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input data-testid="name-input" value={searchCriteria.name} onChange={(e) => setSearchCriteria({ ...searchCriteria, name: e.target.value })} placeholder="Enter name" />
                </div>

                <div className="space-y-2">
                  <Label>State</Label>
                  <Select value={searchCriteria.state || 'all_states'} onValueChange={(value) => {
                    const newState = value === 'all_states' ? '' : value;
                    setSearchCriteria({ ...searchCriteria, state: newState, district: '' });
                    setDistricts([]);
                    if (newState) fetchDistricts(newState);
                  }}>
                    <SelectTrigger data-testid="state-input">
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_states">All States</SelectItem>
                      {states.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>District</Label>
                  <Select value={searchCriteria.district || 'all_districts'} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, district: value === 'all_districts' ? '' : value })} disabled={!searchCriteria.state}>
                    <SelectTrigger data-testid="district-input">
                      <SelectValue placeholder={searchCriteria.state ? 'All Districts' : 'Select state first'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_districts">All Districts</SelectItem>
                      {districts.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input data-testid="end-date-input" type="month" value={searchCriteria.end_date} onChange={(e) => setSearchCriteria({ ...searchCriteria, end_date: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input data-testid="company-name-input" value={searchCriteria.company_name} onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })} placeholder="Enter company name" />
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="email-input" value={searchCriteria.email} onChange={(e) => setSearchCriteria({ ...searchCriteria, email: e.target.value })} placeholder="Enter email" />
                </div>
              </div>

                <div className="flex space-x-4">
                  <Button type="submit" disabled={loading} className="btn-primary" style={{ backgroundColor: '#059669' }} data-testid="generate-button">
                    <Search className="mr-2" size={18} />
                    {loading ? 'Generating...' : 'Generate Labels'}
                  </Button>
                  <Button type="button" onClick={handleReset} variant="outline" data-testid="reset-button">
                    Reset
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleMultipleIdsSearch}>
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#059669' }}>
                  Enter Multiple Advertiser IDs
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Enter up to 25 advertiser IDs to generate labels for multiple advertisers at once.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                  {multipleIds.map((id, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="flex-1">
                        <Input
                          value={id}
                          onChange={(e) => updateIdField(index, e.target.value)}
                          placeholder={`Advertiser ID ${index + 1}`}
                          className="w-full"
                        />
                      </div>
                      {multipleIds.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeIdField(index)}
                          className="text-red-500 hover:text-red-700 flex-shrink-0"
                        >
                          <X size={18} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex space-x-4">
                  {multipleIds.length < 25 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addIdField}
                      className="border-green-500 text-green-600 hover:bg-green-50"
                    >
                      <Plus className="mr-2" size={18} />
                      Add More ID ({multipleIds.length}/25)
                    </Button>
                  )}
                  <Button type="submit" disabled={loading} className="btn-primary" style={{ backgroundColor: '#059669' }}>
                    <Search className="mr-2" size={18} />
                    {loading ? 'Generating...' : 'Generate Labels'}
                  </Button>
                  <Button type="button" onClick={handleReset} variant="outline">
                    Reset
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}

        {labelsData.length > 0 && (
          <>
            <div className="mb-4 no-print flex items-center space-x-4 flex-wrap gap-y-2">
              <Button onClick={handlePrint} className="btn-primary" style={{ backgroundColor: '#059669' }} data-testid="print-labels-button">
                <Printer className="mr-2" size={18} />
                Print Labels
              </Button>
              <label className="inline-flex items-center gap-2 cursor-pointer bg-green-50 border border-green-200 px-3 py-2 rounded-md">
                <input
                  type="checkbox"
                  checked={showLastShipment}
                  onChange={(e) => setShowLastShipment(e.target.checked)}
                  data-testid="toggle-last-shipment"
                  className="w-4 h-4"
                />
                <span className="text-sm font-semibold">Show Last Shipment on Label</span>
              </label>
              <Button
                onClick={() => {
                  setLabelsData([]);
                  setSearchCriteria({ magazine: '', status: '', advertiser_id: '', name: '', company_name: '', email: '', state: '', district: '', end_date: '' });
                }}
                variant="outline"
                data-testid="new-search-button"
              >
                New Search
              </Button>
            </div>

            <div className="label-container" data-testid="labels-container">
              {labelsData.map((advertiser, index) => {
                // Calculate if address is long (needs smaller font)
                const addressLength = (advertiser.address || '').length + (advertiser.district || '').length + (advertiser.state || '').length;
                const isLongAddress = addressLength > 100;
                const addressFontSize = isLongAddress ? '10px' : '12px';
                const mobileFontSize = isLongAddress ? '10px' : '12px';
                
                return (
                <div key={index} className="label-item" data-testid={`label-${advertiser.advertiser_id}`}>
                  <div className="flex justify-between items-start" style={{ maxWidth: '100%' }}>
                    <div className="flex-1 pr-2" style={{ minWidth: 0, overflow: 'hidden' }}>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-bold" style={{ color: '#059669' }}>
                          {advertiser.advertiser_id}
                        </span>
                        {advertiser.delivery_mode && (
                          <span className="text-xs font-bold">
                            * {deliveryLabel(advertiser.delivery_mode)} {advertiser.end_month && `(${advertiser.end_month} ${advertiser.end_year})`}
                          </span>
                        )}
                      </div>
                      <div className="text-xs font-bold">To,</div>
                      <div className="font-bold" style={{ fontSize: '13px' }}>
                        {advertiser.initials && <span>{advertiser.initials} </span>}
                        {advertiser.name}
                      </div>
                      {advertiser.designation && <div className="font-bold" style={{ fontSize: addressFontSize }}>{advertiser.designation}</div>}
                      {advertiser.company_name && <div className="font-bold mt-1" style={{ fontSize: addressFontSize }}>{advertiser.company_name}</div>}
                      {advertiser.address && <div className="font-bold mt-1" style={{ fontSize: addressFontSize, wordBreak: 'break-word' }}>{advertiser.address}</div>}
                      <div className="font-bold" style={{ fontSize: addressFontSize }}>
                        {advertiser.district && `${advertiser.district} `}
                        {advertiser.state}
                        {advertiser.pincode && ` ${advertiser.pincode}`}
                      </div>
                      {advertiser.contact_no && (
                        <div className="font-bold mt-1" style={{ fontSize: mobileFontSize, wordBreak: 'break-word' }}>M. {advertiser.contact_no}</div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end">
                      <img src={advertiser.qr_code} alt="QR Code" className="w-16 h-16" />
                      {/* Last Shipment Box — hidden by page-level toggle */}
                      {showLastShipment && dispatchInfo[advertiser.advertiser_id] && (
                        <div className="mt-1 border border-gray-300 p-1 text-center" style={{ minWidth: '95px', fontSize: '7px', lineHeight: '1.4' }}>
                          <div className="font-bold" style={{ fontSize: '9px' }}>Last Shipment.</div>
                          {(dispatchInfo[advertiser.advertiser_id].month || dispatchInfo[advertiser.advertiser_id].year) && (
                            <div className="font-semibold" style={{ fontSize: '8px' }}>
                              {dispatchInfo[advertiser.advertiser_id].month} {dispatchInfo[advertiser.advertiser_id].year} Issue
                            </div>
                          )}
                          {dispatchInfo[advertiser.advertiser_id].dispatch_date && (
                            <div style={{ fontSize: '8px' }}>{dispatchInfo[advertiser.advertiser_id].dispatch_date}</div>
                          )}
                          {dispatchInfo[advertiser.advertiser_id].courier_type && (
                            <div className="font-bold" style={{ fontSize: '9px', marginTop: '2px', lineHeight: '1.1' }}>{deliveryLabel(dispatchInfo[advertiser.advertiser_id].courier_type)}</div>
                          )}
                          {dispatchInfo[advertiser.advertiser_id].delivery_status && (
                            <div className={dispatchInfo[advertiser.advertiser_id].delivery_status === 'failed' ? 'text-red-600 font-semibold' : ''} style={{ fontSize: '8px' }}>
                              {dispatchInfo[advertiser.advertiser_id].delivery_status === 'success' ? '✓ Delivered' : '✗ Failed'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );})}
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          * {
            color: black !important;
          }
          .text-gray-600, .text-gray-700 {
            color: #4a4a4a !important;
          }
          button, .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintAdvertiserLabel;